import { useEffect, useState } from "react";
import { Check, Inbox, X } from "lucide-react";
import { api, ApiError } from "../../api";
import { Button, Card, cx } from "../../ui";

type Status = "pending" | "approved" | "rejected";

type Claim = {
  id: string;
  buildingId: string;
  buildingName: string;
  unitId: string;
  unitNumber: string;
  claimantEmail: string;
  claimantFirstName: string;
  claimantLastName: string;
  claimantPhone: string | null;
  note: string | null;
  status: Status;
  decidedAt: string | null;
  decidedReason: string | null;
  createdAt: string;
};

const STATUS_TABS: Array<{ key: Status; label: string }> = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export function AdminClaims() {
  const [tab, setTab] = useState<Status>("pending");
  const [rows, setRows] = useState<Claim[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = async () => {
    setRows(null);
    try {
      const r = await api.get<{ data: Claim[] }>(
        `/api/admin/unit-claims?status=${tab}`,
      );
      setRows(r.data);
    } catch (err) {
      setError(String(err));
      setRows([]);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const approve = async (claim: Claim) => {
    if (!window.confirm(`Approve ${claim.claimantEmail} for Unit ${claim.unitNumber}? This will email them an invite code.`)) {
      return;
    }
    setBusyId(claim.id);
    try {
      await api.post(`/api/admin/unit-claims/${claim.id}/approve`, {
        roleCode: "unit_owner",
      });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? JSON.stringify(err.body) : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (claim: Claim) => {
    const reason = window.prompt(
      `Reject ${claim.claimantEmail}'s claim on Unit ${claim.unitNumber}. Reason (sent to the claimant):`,
    );
    if (!reason || reason.trim().length === 0) return;
    setBusyId(claim.id);
    try {
      await api.post(`/api/admin/unit-claims/${claim.id}/reject`, {
        reason: reason.trim(),
      });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? JSON.stringify(err.body) : String(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <h2 className="text-ink-800 m-0 mb-3 inline-flex items-center gap-2">
        <Inbox size={18} /> Unit claims
      </h2>
      <p className="text-[13px] text-ink-600 mb-4">
        Self-serve onboarding requests. Approving issues an invite code +
        emails it to the claimant. Rejecting sends them your reason.
      </p>

      <div className="flex gap-1 mb-4 overflow-x-auto -mx-1 px-1">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cx(
              "px-3.5 py-2 rounded-md text-[13px] font-medium whitespace-nowrap transition-colors",
              tab === t.key
                ? "bg-brand-50 text-brand-700 border border-brand-200"
                : "bg-transparent text-ink-600 hover:text-ink-800 hover:bg-sand-50 border border-transparent",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="alert-error mb-3">{error}</div>}

      {rows === null ? (
        <p className="text-ink-500 text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-ink-500 text-sm">
          {tab === "pending"
            ? "No pending claims."
            : tab === "approved"
              ? "No approved claims yet."
              : "No rejected claims."}
        </p>
      ) : (
        <ul className="divide-y divide-sand-100">
          {rows.map((c) => (
            <li key={c.id} className="py-3.5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-ink-800">
                    {c.claimantFirstName} {c.claimantLastName}
                    <span className="ml-2 text-[12.5px] font-normal text-ink-500">
                      {c.claimantEmail}
                    </span>
                  </div>
                  <div className="text-[12.5px] text-ink-600 mt-0.5">
                    {c.buildingName} · Unit {c.unitNumber}
                    {c.claimantPhone ? ` · ${c.claimantPhone}` : ""}
                  </div>
                  <div className="text-[11.5px] text-ink-400 mt-0.5">
                    Submitted {new Date(c.createdAt).toLocaleString()}
                  </div>
                  {c.note && (
                    <div className="text-[12.5px] text-ink-600 mt-2 bg-sand-50 border border-sand-100 rounded-md px-3 py-2 whitespace-pre-wrap">
                      {c.note}
                    </div>
                  )}
                  {c.decidedAt && (
                    <div className="text-[11.5px] text-ink-500 mt-2">
                      {c.status === "approved" ? "Approved" : "Rejected"}{" "}
                      {new Date(c.decidedAt).toLocaleString()}
                      {c.decidedReason && ` — ${c.decidedReason}`}
                    </div>
                  )}
                </div>
                {c.status === "pending" && (
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => void approve(c)}
                      disabled={busyId === c.id}
                    >
                      <Check size={14} />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void reject(c)}
                      disabled={busyId === c.id}
                    >
                      <X size={14} />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
